import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { fetchGallerySettings, mergeGallery } from "@/lib/gallery";

export const Route = createFileRoute("/gallery")({
  loader: async () => {
    const settings = await fetchGallerySettings();
    return { items: mergeGallery(settings, true) };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: "Project Gallery | Home Theater & Smart Home Work in Denver — Apex AV" },
      { name: "description", content: "Browse photos of Apex AV home theaters, whole-home audio, lighting, shading, networking, and smart home installs across Denver and the Front Range." },
      { property: "og:title", content: "Project Gallery | Apex AV Denver" },
      { property: "og:description", content: "Photos of home theater, audio, lighting, and smart home installs by Apex AV in Denver." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/gallery" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ImageGallery",
              "@id": "https://apexav.com/gallery#gallery",
              name: "Apex AV Project Gallery",
              description:
                "Photos of home theater, whole-home audio, lighting, shading, networking, and smart home installs by Apex AV across Denver and the Front Range.",
              url: "https://apexav.com/gallery",
              author: { "@id": "https://apexav.com/#organization" },
              image: (loaderData?.items ?? []).map((im) => ({
                "@type": "ImageObject",
                contentUrl: im.loc.startsWith("http") ? im.loc : `https://apexav.com${im.loc}`,
                name: im.displayTitle,
                ...(im.alt ? { description: im.alt } : {}),
              })),
            },
            {
              "@type": "BreadcrumbList",
              "@id": "https://apexav.com/gallery#breadcrumb",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://apexav.com/" },
                { "@type": "ListItem", position: 2, name: "Gallery", item: "https://apexav.com/gallery" },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: GalleryPage,
});

function GalleryPage() {
  const { items } = Route.useLoaderData();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const count = items.length;
  const go = useCallback((d: number) => setIndex((i) => (i + d + count) % count), [count]);

  useEffect(() => {
    if (!playing || count < 2) return;
    const t = setInterval(() => go(1), 5000);
    return () => clearInterval(t);
  }, [playing, go, count]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  const current = items[index];

  return (
    <section className="container mx-auto px-6 py-16">
      <div className="eyebrow text-brand-text">Our work</div>
      <h1 className="mt-3 font-display text-4xl font-bold text-foreground md:text-5xl">Project gallery</h1>
      <p className="mt-4 max-w-2xl text-muted-foreground">
        Home theaters, audio, lighting, shading, and networking installed by Apex AV across Denver and the Front Range.
      </p>

      {count === 0 ? (
        <p className="mt-12 text-muted-foreground">Photos coming soon.</p>
      ) : (
        <>
          <div className="relative mt-10 overflow-hidden rounded-md border border-border bg-card">
            <div className="relative aspect-[16/9] w-full">
              {items.map((im, i) => (
                <img
                  key={im.key}
                  src={im.loc}
                  alt={im.alt}
                  title={im.displayTitle}
                  loading={i === 0 ? "eager" : "lazy"}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
                  aria-hidden={i !== index}
                />
              ))}
            </div>
            <button aria-label="Previous photo" onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-border bg-background/70 p-2 text-foreground backdrop-blur hover:bg-background">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button aria-label="Next photo" onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-border bg-background/70 p-2 text-foreground backdrop-blur hover:bg-background">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div aria-live="polite">
              <h2 className="font-display text-xl font-semibold text-foreground">{current.displayTitle}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{current.displayCaption}</p>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="tabular-nums">{index + 1} / {count}</span>
              <button onClick={() => setPlaying((p) => !p)} aria-label={playing ? "Pause slideshow" : "Play slideshow"} className="rounded-full border border-border p-2 hover:bg-accent">
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8">
            {items.map((im, i) => (
              <button
                key={im.key}
                onClick={() => { setIndex(i); setPlaying(false); }}
                aria-label={`Show ${im.displayTitle}`}
                className={`overflow-hidden rounded-sm border ${i === index ? "border-primary" : "border-border opacity-70 hover:opacity-100"}`}
              >
                <img src={im.loc} alt="" loading="lazy" className="aspect-square w-full object-cover" />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
